import TYPES from './types';
import OrdersService from 'src/services/orders/OrdersService';
import OrderModel from 'src/model/order.model';
import OrderLineModel, { ITrackingInfo } from 'src/model/orderline.model';
import PartReturnModel from 'src/model/partReturn.model';
import { IOrders } from 'src/model/modules/orders.model';
import { IOrdersError } from './reducers';
import { String } from 'lodash';

const setIsPending = (isPending: boolean) => {
  return {
    type: TYPES.SET_IS_PENDING,
    payload: isPending ? isPending : false
  };
};

const setError = (error?: IOrdersError) => {
  return {
    type: TYPES.SET_ERROR,
    payload: error
      ? error
      : {
          hasFailed: false,
          message: ''
        }
  };
};

const appendOrder = (order: IOrders) => {
  return {
    type: TYPES.SET_ORDER,
    payload: order
  };
};

const appendTrackingNumber = (tn: ITrackingInfo) => {
  return {
    type: TYPES.SET_TRACKING_NUMBER,
    payload: tn
  };
};

const setRecentOrders = (orders: IOrders[]) => {
  return {
    type: TYPES.SET_RECENT_ORDERS,
    payload: orders
  };
};

const setPageNumber = (pageNumber: number) => {
  return {
    type: TYPES.SET_PAGE_NUMBER,
    payload: pageNumber ? pageNumber : 0
  };
};

const setPageSize = (pageSize: number) => {
  return {
    type: TYPES.SET_PAGE_SIZE,
    payload: pageSize ? pageSize : 0
  };
};

const setTotalElements = (totalElements: number) => {
  return {
    type: TYPES.SET_TOTAL_ELEMENTS,
    payload: totalElements ? totalElements : 0
  };
};

const setTotalPages = (totalPages: number) => {
  return {
    type: TYPES.SET_TOTAL_PAGES,
    payload: totalPages ? totalPages : 0
  };
};

const clearOrders = () => {
  return {
    type: TYPES.CLEAR_ALL
  };
};

const formatOrder = (order: OrderModel): IOrders => {
  let invoices = {};
  order.orderDetails.forEach((orderLine: OrderLineModel) => {
    if (orderLine.backorder) {
      if (!invoices['backorder']) {
        invoices = { ...invoices, ['backorder']: [orderLine] };
      } else {
        invoices['backorder'].push(orderLine);
      }
    } else {
      if (!invoices[orderLine.invoiceNumber]) {
        invoices = { ...invoices, [orderLine.invoiceNumber]: [orderLine] };
      } else {
        invoices[orderLine.invoiceNumber].push(orderLine);
      }
    }
  });
  const neworder = { ...order, invoices };
  // delete neworder.orderDetails;
  delete neworder.invoiceNumbers;
  return neworder;
};

const cancelOrder = (
  orderNumber: number,
  items: { [key: number]: string },
  currentOrdersData: {
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    sort?: string;
    status?: string;
    size?: number;
    page?: number;
  }
) => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    OrdersService.cancelOrder(orderNumber, items)
      .then(response => {
        const {
          dateFrom,
          dateTo,
          search,
          sort,
          status,
          size,
          page
        } = currentOrdersData;
        dispatch(setIsPending(false));
        dispatch(getInfo(dateFrom, dateTo, search, sort, status, size, page));
        return response;
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error canceling orders'
          })
        );
        console.error('ERROR', error);
      });
  };
};

const getInfo = (
  dateFrom?: string,
  dateTo?: string,
  search?: string,
  sort?: string,
  status?: string,
  size?: number,
  page?: number
) => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    OrdersService.getOrders(dateFrom, dateTo, search, sort, status, size, page)
      .then(response => {
        if (response.data) {
          return response;
        } else {
          return null;
        }
      })
      .then((response: any) => {
        dispatch(clearOrders());
        if (response.data) {
          response.data.forEach((order: OrderModel) => {
            let formatedOrder: IOrders = {
              invoices: {},
              orderDate: '',
              orderNumber: null,
              poNumber: '',
              status: '',
              cancellationStatus: 0,
              confimation: '',
              creditOrder: false
            };
            formatedOrder = formatOrder(order);
            dispatch(appendOrder(formatedOrder));
          });
          dispatch(setPageNumber(response.pageNumber));
          dispatch(setPageSize(response.pageSize));
          dispatch(setTotalElements(response.totalElements));
          dispatch(setTotalPages(response.totalPages));
          dispatch(setIsPending(false));
        } else {
          dispatch(setPageNumber(0));
          dispatch(setPageSize(0));
          dispatch(setTotalElements(0));
          dispatch(setTotalPages(0));
        }
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error getting orders info'
          })
        );
        console.error('ERROR', error);
      });
  };
};

const getRecentOrders = () => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    OrdersService.getRecentOrders(10)
      .then(response => {
        dispatch(setIsPending(false));
        if (response.data) {
          const orders = response.data.map(order => formatOrder(order));
          dispatch(setRecentOrders(orders));
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error getting orders info'
          })
        );
      });
  };
};

const getTracking = (
  lineNumber: number,
  orderDate: number,
  orderNumber: number,
  orderDetailStatus: string
) => {
  return (dispatch: any) => {
    if (orderDetailStatus === 'Invoiced') {
      OrdersService.getTracking(lineNumber, orderDate, orderNumber)
        .then(data => {
          data = { lineNumber, orderDate, orderNumber, ...data };
          dispatch(appendTrackingNumber(data));
        })
        .catch(err => {
          console.error(err);
        });
    }
  };
};

export default {
  appendOrder,
  setError,
  setIsPending,
  getInfo,
  setPageNumber,
  setPageSize,
  setTotalElements,
  setTotalPages,
  clearOrders,
  getRecentOrders,
  appendTrackingNumber,
  getTracking,
  cancelOrder
};
